import { create } from "zustand";
import type { ConnectionMethod, DeployMode, Family } from "../../../api/types";

/* Only the in-flight form values live here; everything the operator confirms is
   committed to the server, so a reload resumes from the entity row. */

export const DEFAULT_SILENCE_MINUTES = 1440;
// The draft stores family keys (sales), never the display names.
export { FAMILIES } from "../../../domain/families";

export type IdentityDraft = {
  legalName: string;
  crNumber: string;
  vatin: string;
  erpType: string;
  erpVersion: string;
  wave: string;
  /** Wire name kept; the values are family keys. */
  enabledDocTypes: Family[];
  deployMode: DeployMode;
  silenceThresholdMinutes: number;
};

export type ConnectionDraft = {
  method: ConnectionMethod | null;
  config: Record<string, string>;
};

export const EMPTY_IDENTITY: IdentityDraft = {
  legalName: "",
  crNumber: "",
  vatin: "",
  erpType: "erpnext",
  erpVersion: "",
  wave: "",
  enabledDocTypes: ["sales"],
  deployMode: "hub",
  silenceThresholdMinutes: DEFAULT_SILENCE_MINUTES,
};

export interface OnboardDraft {
  identity: IdentityDraft;
  connection: ConnectionDraft;
  /** Whether a company's test document validated, per family (`testDocumentKey`); the server keeps no such flag. */
  testDocument: Record<string, boolean>;
  setIdentity(patch: Partial<IdentityDraft>): void;
  setMethod(method: ConnectionMethod): void;
  setConfig(patch: Record<string, string>): void;
  setTestDocument(entityId: string, family: Family, passed: boolean): void;
  reset(): void;
}

export const useOnboardDraft = create<OnboardDraft>((set) => ({
  identity: EMPTY_IDENTITY,
  connection: { method: null, config: {} },
  testDocument: {},
  setTestDocument: (entityId, family, passed) =>
    set((state) => ({ testDocument: { ...state.testDocument, [testDocumentKey(entityId, family)]: passed } })),
  setIdentity: (patch) => set((state) => ({ identity: { ...state.identity, ...patch } })),
  setMethod: (method) => set((state) => ({ connection: { ...state.connection, method } })),
  setConfig: (patch) =>
    set((state) => ({ connection: { ...state.connection, config: { ...state.connection.config, ...patch } } })),
  reset: () =>
    set({ identity: EMPTY_IDENTITY, connection: { method: null, config: {} }, testDocument: {} }),
}));

/** The draft remembers one dry run per company per family (HUB-F13). */
export function testDocumentKey(entityId: string, family: Family): string {
  return `${entityId}:${family}`;
}

/** Peppol participant for an Omani CR; recomputed as the CR is typed. */
export function participantFor(cr: string): string {
  return `0248:${cr.trim()}`;
}

/** A URL-safe company code; `POST /api/entities` requires one and `entity.code` is unique.
    A name with no latin letters (Arabic, say) slugifies to nothing, so the CR carries it. */
export function codeFor(legalName: string, cr = ""): string {
  const slug = legalName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return slug || `om-${cr.trim()}`;
}

/** Two companies can share a name; the CR cannot, so it breaks a code collision. */
export function codeWithCr(code: string, cr: string): string {
  return `${code}-${cr.trim()}`.slice(0, 60);
}

export const VATIN_PATTERN = /^OM\d{10}$/;
